/**
 * Chat hook with pluggable transport support.
 *
 * Follows AI SDK v6 patterns:
 * - Transport abstraction for SSE/WebSocket
 * - Input state decoupled from hook (v5+ pattern)
 * - Tool output support via addToolOutput
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  ChatStatus,
  UIMessage,
  MessagePart,
  TextPart,
  ReasoningPart,
  MemoryContextPart,
  MemoryItem,
  PlanStatusPart,
  AudioDataPart,
  StructuredOutputDataPart,
  ToolInvocationPart,
  TypedToolPart,
  ToolState,
  DebugEntry,
  PlanStatusDebugEntry,
  MCPToolInvocationPart,
  MCPToolState,
  StreamEventDebugEntry,
  ActivityPart,
  ActivityEventData,
} from '../types/chat';
import type { Message } from '../types';
import type { PlanningStrategy } from '../types/planning';
import type { ChatTransport, StreamEvent } from '../transport/ChatTransport';
import { SSETransport } from '../transport/SSETransport';
import type {
  CanvasUpdateData,
  CanvasCompleteData,
  CanvasErrorData,
  ComponentTree,
} from '../types/canvas';
const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Categorize stream event types for debug display.
 */
export function categorizeEventType(
  eventType: string,
): StreamEventDebugEntry['category'] {
  if (
    eventType.startsWith('text') ||
    eventType === 'message_delta' ||
    eventType === 'message_complete'
  ) {
    return 'text';
  }
  if (eventType.startsWith('reasoning')) {
    return 'reasoning';
  }
  if (eventType.startsWith('tool') || eventType === 'tool-invocation') {
    return 'tool';
  }
  if (
    eventType === 'data-fileAdded' ||
    eventType === 'data-fileDeleted' ||
    eventType === 'data-filesList'
  ) {
    return 'file';
  }
  // Canvas events get their own category (T068)
  if (
    eventType === 'data-canvas-update' ||
    eventType === 'data-canvas-complete' ||
    eventType === 'data-canvas-error'
  ) {
    return 'canvas';
  }
  if (eventType === 'data-activity') {
    return 'activity';
  }
  if (eventType.startsWith('data-')) {
    return 'data';
  }
  if (eventType === 'error') {
    return 'error';
  }
  return 'lifecycle';
}

export interface PlanStatusEvent {
  object: 'plan_status';
  event_type: 'plan_create' | 'plan_update' | 'plan_complete';
  strategy: PlanningStrategy;
  status: Record<string, unknown>;
}

export interface FileEvent {
  type: 'file-added' | 'file-deleted';
  data: {
    filename?: string;
    path: string;
    size?: number;
    mimeType?: string;
  };
}

/**
 * Normalize a server-side message ``content`` field to a plain string.
 * See ``useChatLegacy.normalizeMessageContent`` for the rationale —
 * server payloads can be string / array of blocks / object, the
 * legacy regex parser assumed ``string`` and crashed on the others.
 */
function normalizeMessageContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (content == null) return '';
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object') {
          const text = (block as { text?: unknown }).text;
          if (typeof text === 'string') return text;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof content === 'object') {
    const text = (content as { text?: unknown }).text;
    if (typeof text === 'string') return text;
  }
  return String(content);
}

function parseMemoryFromText(text: string): {
  memories: MemoryItem[];
  cleanText: string;
} {
  const memoryRegex = /<long_term_memory>([\s\S]*?)<\/long_term_memory>/g;
  const memories: MemoryItem[] = [];
  let cleanText = text;

  let match;
  while ((match = memoryRegex.exec(text)) !== null) {
    const memoryContent = match[1].trim();
    const lines = memoryContent.split('\n').filter((line) => line.trim());

    for (const line of lines) {
      const trimmedLine = line
        .replace(
          /^The content below are retrieved from long-term memory.*?:\s*/i,
          '',
        )
        .trim();
      if (!trimmedLine) continue;

      const roleMatch = trimmedLine.match(/^\[(\w+)\]\s*(.+)$/);
      const content = roleMatch ? roleMatch[2] : trimmedLine;

      if (content && !trimmedLine.startsWith('The content below')) {
        memories.push({
          id: `memory-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          content,
          scope: 'user',
          memoryType: 'personal',
          confidence: 1.0,
          source: 'agent_recorded',
          relevanceScore: 1.0,
        });
      }
    }

    cleanText = cleanText.replace(match[0], '').trim();
  }

  return { memories, cleanText };
}

function convertBackendMessages(messages: Message[] | undefined): UIMessage[] {
  if (!messages || messages.length === 0) return [];

  const result: UIMessage[] = [];

  for (const msg of messages) {
    // Support both new (content/timestamp) and legacy (text/created_at) fields.
    // ``content`` may be a string, a JSON-stringified payload, or an array
    // of content blocks (voice transcripts, tool outputs); coerce before
    // the regex-based memory extractor sees it.
    const rawContent: unknown = msg.content ?? msg.text ?? '';
    const messageText = normalizeMessageContent(rawContent);
    const messageTimestamp = msg.timestamp || msg.created_at || new Date().toISOString();
    const { memories, cleanText } = parseMemoryFromText(messageText);

    if (memories.length > 0 && !cleanText.trim()) {
      result.push({
        id: `msg-${msg.id}-memory`,
        role: 'assistant',
        parts: [
          {
            type: 'data-memoryContext',
            data: { memories },
          } as MemoryContextPart,
        ],
        createdAt: new Date(messageTimestamp),
      });
      continue;
    }

    const parts: MessagePart[] = [];

    if (memories.length > 0) {
      parts.push({
        type: 'data-memoryContext',
        data: { memories },
      } as MemoryContextPart);
    }

    if (cleanText.trim()) {
      parts.push({ type: 'text', text: cleanText } as TextPart);
    }

    if (parts.length > 0) {
      result.push({
        id: `msg-${msg.id}`,
        role: msg.role === 'user' || msg.sender === 'user' ? 'user' : 'assistant',
        parts,
        createdAt: new Date(messageTimestamp),
      });
    }
  }

  return result;
}

// Canvas event types are re-exported from canvas.ts for backwards compatibility
export type CanvasUpdateEvent = CanvasUpdateData;
export type CanvasCompleteEvent = CanvasCompleteData;
export type CanvasErrorEvent = CanvasErrorData;

export interface ToolsAvailableEvent {
  tools: Array<{ name: string; description: string; group: string }>;
}

export interface SystemPromptEvent {
  sections: Array<{ label: string; content: string }>;
  hasCxsContext: boolean;
  serviceId: string | null;
}

export interface SystemPromptInjectionsEvent {
  injections: Array<{ source: string; role: string; content: string }>;
}

export interface UseChatOptions {
  /** Session/chat identifier */
  sessionId: string | null;
  /** User identifier */
  userId: string | null;
  /** Optional organization identifier */
  organizationId?: string | null;
  /** Optional transport instance (defaults to SSETransport) */
  transport?: ChatTransport;
  /** Callback when plan status updates */
  onPlanStatusUpdate?: (event: PlanStatusEvent) => void;
  /** Callback when file events occur */
  onFileEvent?: (event: FileEvent) => void;
  /** Callback when canvas update event received */
  onCanvasUpdate?: (event: CanvasUpdateEvent) => void;
  /** Callback when canvas complete event received */
  onCanvasComplete?: (event: CanvasCompleteEvent) => void;
  /** Callback when canvas error event received */
  onCanvasError?: (event: CanvasErrorEvent) => void;
  /** Callback when tools_available event received */
  onToolsAvailable?: (event: ToolsAvailableEvent) => void;
  /** Callback when system prompt inspection data received */
  onSystemPrompt?: (event: SystemPromptEvent) => void;
  /** Callback when system prompt injection data received */
  onSystemPromptInjections?: (event: SystemPromptInjectionsEvent) => void;
  /** Extra fields to include in the request body */
  extraBody?: Record<string, unknown>;
  /** Initial messages to populate */
  initialMessages?: Message[];
}

export interface UseChatReturn {
  /** Current messages */
  messages: UIMessage[];
  /** Current chat status */
  status: ChatStatus;
  /** Error if any */
  error: Error | null;
  /** Whether input should be disabled */
  isInputDisabled: boolean;
  /** Send a message */
  sendMessage: (text: string) => Promise<void>;
  /** Stop streaming */
  stop: () => Promise<void>;
  /** Reload last exchange */
  reload: () => Promise<void>;
  /** Retry after error */
  retry: () => Promise<void>;
  /** Debug entries for debugging panel */
  debugEntries: DebugEntry[];
  /** Add a debug entry */
  addDebugEntry: (entry: Omit<DebugEntry, 'id' | 'timestamp'>) => void;
  /** Submit tool output (if transport supports it) */
  addToolOutput?: (
    toolCallId: string,
    output: unknown,
    isError?: boolean,
  ) => Promise<void>;
  /** Tool timing data for usage metrics */
  toolTimings: Map<string, { startedAt: number; completedAt?: number }>;
  /** Enriched tool metadata for unified tool card display */
  toolMetadata: Map<
    string,
    {
      startedAt?: number;
      completedAt?: number;
      durationMs?: number;
      category?: string;
      serverName?: string;
      parentToolCallId?: string;
    }
  >;
  /** Handle a voice event through the same pipeline as text chat */
  handleVoiceEvent: (event: StreamEvent) => void;
  /** Timestamp of the last agent push message (for auto-scroll) */
  lastPushTimestamp: number;
  /** Append a local-only assistant message (not sent/persisted) */
  injectAssistantMessage: (text: string) => void;
}

/**
 * Chat hook with pluggable transport.
 *
 * Note: Input state is NOT managed by this hook (AI SDK v5+ pattern).
 * Manage input state externally in your component.
 */
export function useChat(options: UseChatOptions): UseChatReturn {
  const {
    sessionId,
    userId,
    organizationId,
    transport,
    onPlanStatusUpdate,
    onFileEvent,
    onCanvasUpdate,
    onCanvasComplete,
    onCanvasError,
    onToolsAvailable,
    onSystemPrompt,
    onSystemPromptInjections,
    extraBody,
    initialMessages,
  } = options;

  // Create default transport if not provided
  const transportRef = useRef<ChatTransport>(
    transport || new SSETransport({ baseUrl: AGENT_URL }),
  );

  // Update transport ref if provided transport changes
  useEffect(() => {
    if (transport) {
      transportRef.current = transport;
    }
  }, [transport]);

  const [messages, setMessages] = useState<UIMessage[]>(() =>
    convertBackendMessages(initialMessages),
  );
  const [status, setStatus] = useState<ChatStatus>('ready');
  const [error, setError] = useState<Error | null>(null);
  const [debugEntries, setDebugEntries] = useState<DebugEntry[]>([]);
  const [lastPushTimestamp, setLastPushTimestamp] = useState<number>(0);

  // Listen for agent push messages (proactive updates from backend monitors)
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const { content, messageId, canvasComponents, usage, agentSlug } = detail;
      const text =
        content != null && typeof content === 'string'
          ? content.trim()
          : String(content ?? '').trim();
      const hasCanvas =
        canvasComponents &&
        Array.isArray(canvasComponents) &&
        canvasComponents.length > 0;
      const hasUsage = usage && typeof usage === 'object';
      if (!text && !hasCanvas && !hasUsage) return;

      // Build the pushed message's parts: text when present, plus the turn's
      // token usage as an agent-complete activity. A detached/woken turn carries
      // its usage (incl. delegated sub-agents under `children`); when the text
      // was a duplicate the server delivers usage-only so every token is still
      // accounted for in the per-message card + Usage sidebar.
      if (text || hasUsage) {
        const parts: UIMessage['parts'] = [];
        if (text) parts.push({ type: 'text', text: text });
        if (hasUsage) {
          const source =
            (typeof agentSlug === 'string' && agentSlug) || 'assistant';
          parts.push({
            type: 'data-activity',
            id: `push-act-${messageId || Date.now()}`,
            transient: false,
            data: {
              activityType: 'agent-complete',
              ancestry: [source],
              source,
              message: '',
              timestamp: Date.now() / 1000,
              usage,
            },
          });
        }
        const pushMessage: UIMessage = {
          id: messageId || `push-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          role: 'assistant',
          parts,
          createdAt: new Date(),
        };
        setMessages((prev) => [...prev, pushMessage]);
        // Dispatch a separate event for scroll — ChatWindow listens directly
        window.dispatchEvent(new CustomEvent('cxs-push-scroll'));
      }
      setLastPushTimestamp(Date.now());

      // Dispatch canvas components if included in the push
      if (hasCanvas) {
        window.dispatchEvent(
          new CustomEvent('cxs-canvas-push', { detail: canvasComponents }),
        );
        window.dispatchEvent(new CustomEvent('cxs-focus-canvas'));
      }
    };
    window.addEventListener('cxs-agent-push', handler);
    return () => window.removeEventListener('cxs-agent-push', handler);
  }, []);

  const messageQueueRef = useRef<string[]>([]);
  const isProcessingRef = useRef(false);
  const retryCountRef = useRef(0);
  const prevSessionIdRef = useRef<string | null>(null);
  const initialMessagesLoadedRef = useRef(false);
  const activeToolsRef = useRef<Map<string, MCPToolInvocationPart>>(new Map());
  // Track tool names for AI SDK format events (tool-output-available doesn't include toolName)
  const toolNameMap = useRef<Map<string, string>>(new Map());
  // Track tool timing for usage metrics (tool events lack timestamps)
  const toolTimingRef = useRef<Map<string, { startedAt: number; completedAt?: number }>>(new Map());
  // Track enriched tool metadata for unified tool card display
  const toolMetadataRef = useRef<
    Map<
      string,
      {
        startedAt?: number;
        completedAt?: number;
        durationMs?: number;
        category?: string;
        serverName?: string;
        parentToolCallId?: string;
      }
    >
  >(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);
  // TTFT: send time of the in-flight turn, used to stamp first_ui_paint
  // (the first visible token) client-side (spec 067 T006/FR-016).
  const ttftStartRef = useRef<number | null>(null);

  // Reset state on session change
  useEffect(() => {
    const sessionChanged = sessionId !== prevSessionIdRef.current;

    if (sessionChanged) {
      initialMessagesLoadedRef.current = false;
    }

    const messagesNotYetLoaded =
      !initialMessagesLoadedRef.current &&
      initialMessages &&
      initialMessages.length > 0;

    if (sessionChanged || messagesNotYetLoaded) {
      prevSessionIdRef.current = sessionId;
      initialMessagesLoadedRef.current =
        initialMessages !== undefined && initialMessages.length > 0;

      setMessages(convertBackendMessages(initialMessages));
      setDebugEntries([]);
      setError(null);
      setStatus('ready');

      messageQueueRef.current = [];
      isProcessingRef.current = false;
      toolTimingRef.current.clear();
      toolMetadataRef.current.clear();
    }
  }, [sessionId, initialMessages]);

  const addDebugEntry = useCallback(
    (entry: Omit<DebugEntry, 'id' | 'timestamp'>) => {
      setDebugEntries((prev) => [
        ...prev,
        {
          ...entry,
          id: `debug-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timestamp: new Date(),
        },
      ]);
    },
    [],
  );

  /**
   * Append a local-only assistant message without going through the
   * transport/streaming pipeline (e.g. an SSP-configured welcome greeting
   * shown before the user's first message). Never sent to or persisted by
   * the backend.
   */
  const injectAssistantMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        parts: [{ type: 'text', text: trimmed }],
        createdAt: new Date(),
      },
    ]);
  }, []);

  /**
   * Process a stream event and update message state.
   */
  const processStreamEvent = useCallback(
    (
      event: StreamEvent,
      assistantMessageId: string,
      accumulatedText: { value: string },
      accumulatedReasoning: { value: string },
      memoryContext: { value: MemoryItem[] | null },
      hasReceivedToken: { value: boolean },
    ): boolean => {
      const eventType = (event.type as string) || 'unknown';

      const mergeAssistantParts = (msg: UIMessage): MessagePart[] => {
        const preservedParts = msg.parts.filter(
          (part) =>
            part.type !== 'text' &&
            part.type !== 'reasoning' &&
            part.type !== 'data-memoryContext',
        );
        const parts: MessagePart[] = [];
        if (memoryContext.value) {
          parts.push({
            type: 'data-memoryContext',
            data: { memories: memoryContext.value },
          } as MemoryContextPart);
        }
        if (accumulatedReasoning.value) {
          parts.push({
            type: 'reasoning',
            text: accumulatedReasoning.value,
          } as ReasoningPart);
        }
        if (accumulatedText.value) {
          parts.push({
            type: 'text',
            text: accumulatedText.value,
          } as TextPart);
        }
        return [...parts, ...preservedParts];
      };

      // Log all stream events
      addDebugEntry({
        type: 'stream-event',
        content: {
          eventType,
          payload: event as Record<string, unknown>,
          category: categorizeEventType(eventType),
        } as StreamEventDebugEntry,
      });

      // Handle text delta events
      const textDelta =
        event.type === 'text-delta'
          ? event.delta
          : event.type === 'text'
            ? event.text
            : event.type === 'message_delta'
              ? (event.data as Record<string, unknown>)?.delta
              : null;

      if (textDelta) {
        if (!hasReceivedToken.value) {
          hasReceivedToken.value = true;
          setStatus('streaming');
          // first_ui_paint (spec 067 T006): time from send to first visible
          // token, recorded client-side. The server trace's first_ui_paint
          // stays null (no WS round-trip merge); this is the documented
          // client-only fallback.
          if (ttftStartRef.current != null) {
            const firstUiPaintMs = Math.round(
              performance.now() - ttftStartRef.current,
            );
            ttftStartRef.current = null;
            addDebugEntry({
              type: 'stream-event',
              content: {
                eventType: 'ttft-first-ui-paint',
                payload: { firstUiPaintMs },
                category: categorizeEventType('ttft-first-ui-paint'),
              } as StreamEventDebugEntry,
            });
          }
        }

        accumulatedText.value += textDelta as string;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            return { ...msg, parts: mergeAssistantParts(msg) };
          }),
        );
        return false;
      }

      // Handle reasoning events
      if (
        (event.type === 'reasoning-delta' && event.delta) ||
        (event.type === 'reasoning' && event.text)
      ) {
        const reasoningText = (event.delta || event.text) as string;
        if (!hasReceivedToken.value) {
          hasReceivedToken.value = true;
          setStatus('streaming');
        }

        accumulatedReasoning.value += reasoningText;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            return { ...msg, parts: mergeAssistantParts(msg) };
          }),
        );
        return false;
      }

      // Handle memory context
      if (event.type === 'data-memoryContext') {
        memoryContext.value = (
          event.data as { memories: MemoryItem[] }
        )?.memories;
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            const memoryPart: MemoryContextPart = {
              type: 'data-memoryContext',
              id: event.id as string,
              data: { memories: memoryContext.value || [] },
            };
            return { ...msg, parts: [memoryPart, ...msg.parts] };
          }),
        );
        return false;
      }

      // Handle plan status
      if (event.type === 'data-planStatus') {
        const planData = event.data as {
          eventType: string;
          strategy: PlanningStrategy;
          status: Record<string, unknown>;
        };
        const planPart: PlanStatusPart = {
          type: 'data-planStatus',
          id: event.id as string,
          data: {
            eventType: planData.eventType as
              | 'plan_create'
              | 'plan_update'
              | 'plan_complete',
            strategy: planData.strategy,
            status: planData.status,
          },
        };

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            const existingPlanIndex = msg.parts.findIndex(
              (p) => p.type === 'data-planStatus',
            );
            if (existingPlanIndex >= 0) {
              const newParts = [...msg.parts];
              newParts[existingPlanIndex] = planPart;
              return { ...msg, parts: newParts };
            }
            return { ...msg, parts: [...msg.parts, planPart] };
          }),
        );

        const planEvent: PlanStatusEvent = {
          object: 'plan_status',
          event_type: planData.eventType as
            | 'plan_create'
            | 'plan_update'
            | 'plan_complete',
          strategy: planData.strategy,
          status: planData.status,
        };
        onPlanStatusUpdate?.(planEvent);
        return false;
      }

      // Handle tool invocation events - shows tool execution progress
      if (event.type === 'tool-invocation') {
        const toolCallId = (event.toolCallId || event.tool_call_id) as string;
        const toolName = (event.toolName || event.tool_name) as string;
        if (!toolCallId) {
          console.warn('[useChat] Missing toolCallId in tool-invocation event:', event);
          return false;
        }
        const toolArgs = (event.args || {}) as Record<string, unknown>;
        const toolState = (event.state || 'running') as
          | 'running'
          | 'completed'
          | 'failed';
        const toolResult = event.result;

        if (!hasReceivedToken.value) {
          hasReceivedToken.value = true;
          setStatus('streaming');
        }

        // Track tool timing for usage metrics
        if (toolState === 'running' && !toolTimingRef.current.has(toolCallId)) {
          toolTimingRef.current.set(toolCallId, { startedAt: Date.now() });
        } else if (toolState === 'completed' || toolState === 'failed') {
          const existing = toolTimingRef.current.get(toolCallId);
          if (existing) {
            existing.completedAt = Date.now();
          }
        }

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;

            // Find existing tool invocation part by toolCallId
            const existingToolIndex = msg.parts.findIndex(
              (p) =>
                p.type === 'tool-invocation' && p.toolCallId === toolCallId,
            );

            const toolPart: ToolInvocationPart = {
              type: 'tool-invocation',
              toolCallId,
              toolName,
              args: toolArgs,
              state: toolState,
              result: toolResult,
            };

            if (existingToolIndex >= 0) {
              // Update existing tool invocation
              const newParts = [...msg.parts];
              newParts[existingToolIndex] = toolPart;
              return { ...msg, parts: newParts };
            }

            // Add new tool invocation
            return { ...msg, parts: [...msg.parts, toolPart] };
          }),
        );
        return false;
      }

      // Handle AI SDK format tool events (tool-input-start, tool-input-available, tool-output-available)
      // These are used for typed tool parts like A2A delegation
      if (event.type === 'tool-input-start') {
        const toolCallId = event.toolCallId as string;
        const toolName = event.toolName as string;
        if (!toolCallId || !toolName) {
          console.warn('[useChat] Missing required fields in tool-input-start:', event);
          return false;
        }

        if (!hasReceivedToken.value) {
          hasReceivedToken.value = true;
          setStatus('streaming');
        }

        // Track tool name for later use in tool-output-available
        toolNameMap.current.set(toolCallId, toolName);
        // Record tool start time for usage metrics
        toolTimingRef.current.set(toolCallId, { startedAt: Date.now() });

        // Extract enriched metadata from backend event
        // Backend sends time.time() (seconds); JS uses Date.now() (milliseconds)
        const rawStartedAt = event.startedAt as number | undefined;
        const startedAt = rawStartedAt != null ? rawStartedAt * 1000 : undefined;
        const category = event.category as string | undefined;
        const serverName = event.serverName as string | undefined;
        const parentToolCallId = event.parentToolCallId as string | undefined;

        toolMetadataRef.current.set(toolCallId, {
          startedAt: startedAt ?? Date.now(),
          category,
          serverName,
          parentToolCallId,
        });

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;

            const toolPart: TypedToolPart = {
              type: `tool-${toolName}`,
              toolCallId,
              toolName,
              state: 'input-streaming',
            };

            return { ...msg, parts: [...msg.parts, toolPart] };
          }),
        );
        return false;
      }

      if (event.type === 'tool-input-available') {
        const toolCallId = event.toolCallId as string;
        const toolName = event.toolName as string;
        if (!toolCallId || !toolName) {
          console.warn('[useChat] Missing required fields in tool-input-available:', event);
          return false;
        }
        const input = event.input as Record<string, unknown>;

        // Update tool name map
        toolNameMap.current.set(toolCallId, toolName);

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;

            // Find existing typed tool part by toolCallId
            const existingToolIndex = msg.parts.findIndex(
              (p) =>
                p.type.startsWith('tool-') &&
                p.type !== 'tool-invocation' &&
                'toolCallId' in p &&
                p.toolCallId === toolCallId,
            );

            const toolPart: TypedToolPart = {
              type: `tool-${toolName}`,
              toolCallId,
              toolName,
              state: 'input-available',
              input,
            };

            if (existingToolIndex >= 0) {
              const newParts = [...msg.parts];
              newParts[existingToolIndex] = toolPart;
              return { ...msg, parts: newParts };
            }

            return { ...msg, parts: [...msg.parts, toolPart] };
          }),
        );

        return false;
      }

      if (event.type === 'tool-output-available') {
        const toolCallId = event.toolCallId as string;
        if (!toolCallId) {
          console.warn('[useChat] Missing toolCallId in tool-output-available:', event);
          return false;
        }
        const output = event.output;

        // Record tool completion time for usage metrics
        const existingTiming = toolTimingRef.current.get(toolCallId);
        if (existingTiming) {
          existingTiming.completedAt = Date.now();
        }

        // Update enriched metadata with completion timing from backend
        // Backend sends time.time() (seconds); convert to milliseconds
        const rawCompletedAt = event.completedAt as number | undefined;
        const backendCompletedAt = rawCompletedAt != null ? rawCompletedAt * 1000 : Date.now();
        const backendDurationMs = event.durationMs as number | undefined;

        const meta = toolMetadataRef.current.get(toolCallId);
        if (meta) {
          meta.completedAt = backendCompletedAt;
          meta.durationMs =
            backendDurationMs ?? (meta.startedAt ? backendCompletedAt - meta.startedAt : undefined);
        }

        // Get tool name from map (set during input events)
        const toolName = toolNameMap.current.get(toolCallId) || 'unknown';

        // Detect error outputs (backend serializes failed tools as { error: "..." })
        const isError =
          output != null &&
          typeof output === 'object' &&
          !Array.isArray(output) &&
          'error' in (output as Record<string, unknown>);
        const resolvedState: ToolState = isError ? 'output-error' : 'output-available';
        const errorText = isError
          ? String((output as Record<string, unknown>).error)
          : undefined;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;

            // Find existing typed tool part by toolCallId
            const existingToolIndex = msg.parts.findIndex(
              (p) =>
                p.type.startsWith('tool-') &&
                p.type !== 'tool-invocation' &&
                'toolCallId' in p &&
                p.toolCallId === toolCallId,
            );

            if (existingToolIndex >= 0) {
              const existingPart = msg.parts[
                existingToolIndex
              ] as TypedToolPart;
              const toolPart: TypedToolPart = {
                type: existingPart.type,
                toolCallId,
                toolName: existingPart.toolName,
                state: resolvedState,
                input: existingPart.input,
                output,
                errorText,
              };

              const newParts = [...msg.parts];
              newParts[existingToolIndex] = toolPart;
              return { ...msg, parts: newParts };
            }

            // If no existing part found, create new one
            const toolPart: TypedToolPart = {
              type: `tool-${toolName}`,
              toolCallId,
              toolName,
              state: resolvedState,
              output,
              errorText,
            };
            return { ...msg, parts: [...msg.parts, toolPart] };
          }),
        );

        return false;
      }

      // Handle audio events
      if (event.type === 'data-audio') {
        const audioData = event.data as AudioDataPart['data'];
        const audioPart: AudioDataPart = {
          type: 'data-audio',
          id: event.id as string,
          data: audioData,
        };

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            const hasDuplicate = msg.parts.some((part) => {
              if (part.type !== 'data-audio') return false;
              if (audioPart.id && part.id) {
                return part.id === audioPart.id;
              }
              if (
                audioPart.data.chunkIndex !== undefined &&
                part.data.chunkIndex !== undefined
              ) {
                return part.data.chunkIndex === audioPart.data.chunkIndex;
              }
              return false;
            });
            if (hasDuplicate) return msg;
            return { ...msg, parts: [...msg.parts, audioPart] };
          }),
        );
        return false;
      }

      // Handle structured output events
      if (event.type === 'data-structuredOutput') {
        const structuredData = event.data as StructuredOutputDataPart['data'];
        const structuredPart: StructuredOutputDataPart = {
          type: 'data-structuredOutput',
          id: event.id as string,
          data: structuredData,
        };

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            const hasDuplicate = msg.parts.some((part) => {
              if (part.type !== 'data-structuredOutput') return false;
              if (structuredPart.id && part.id) {
                return part.id === structuredPart.id;
              }
              if (structuredPart.data.toolCallId && part.data.toolCallId) {
                return part.data.toolCallId === structuredPart.data.toolCallId;
              }
              return false;
            });
            if (hasDuplicate) return msg;
            return { ...msg, parts: [...msg.parts, structuredPart] };
          }),
        );
        return false;
      }

      // Handle canvas events (T020-T022) - Component-based model
      if (event.type === 'data-canvas-update') {
        const rawData = event.data as {
          canvasId: string;
          componentId?: string;
          artifactId: string;
          jsonTree: unknown;
          action?: string;
          progress?: number;
        };
        const canvasData: CanvasUpdateData = {
          canvasId: rawData.canvasId,
          componentId: rawData.componentId,
          artifactId: rawData.artifactId,
          jsonTree: rawData.jsonTree as ComponentTree,
          action: rawData.action as 'upsert' | 'remove' | undefined,
          progress: rawData.progress,
        };
        onCanvasUpdate?.(canvasData);
        return false;
      }

      if (event.type === 'data-canvas-complete') {
        const rawData = event.data as {
          canvasId: string;
          componentId?: string;
          artifactId: string;
          jsonTree: unknown;
          action?: string;
        };
        const canvasData: CanvasCompleteData = {
          canvasId: rawData.canvasId,
          componentId: rawData.componentId,
          artifactId: rawData.artifactId,
          jsonTree: rawData.jsonTree as ComponentTree,
          action: rawData.action as 'upsert' | 'remove' | undefined,
        };
        onCanvasComplete?.(canvasData);
        return false;
      }

      if (event.type === 'data-canvas-error') {
        const rawData = event.data as {
          canvasId: string;
          componentId?: string;
          artifactId: string;
          error: string;
        };
        const canvasData: CanvasErrorData = {
          canvasId: rawData.canvasId,
          componentId: rawData.componentId,
          artifactId: rawData.artifactId,
          error: rawData.error,
        };
        onCanvasError?.(canvasData);
        return false;
      }

      // Handle activity events (T015)
      if (event.type === 'data-activity') {
        const activityData = event.data as ActivityEventData;
        const isTransient = (event as Record<string, unknown>).transient === true;
        const activityPart: ActivityPart = {
          type: 'data-activity',
          id: event.id as string,
          data: activityData,
          transient: isTransient,
        };

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;

            if (isTransient) {
              // Replace existing transient part with same source (replace-in-place)
              const existingIdx = msg.parts.findIndex(
                (p) =>
                  p.type === 'data-activity' &&
                  (p as ActivityPart).transient &&
                  (p as ActivityPart).data.source === activityData.source,
              );
              if (existingIdx >= 0) {
                const newParts = [...msg.parts];
                newParts[existingIdx] = activityPart;
                return { ...msg, parts: newParts };
              }
            }
            // Append as new part (persistent or first transient for this source)
            return { ...msg, parts: [...msg.parts, activityPart] };
          }),
        );
        return false;
      }

      // Handle file events
      if (event.type === 'data-fileAdded') {
        const fileData = event.data as {
          filename?: string;
          path: string;
          size?: number;
          mimeType?: string;
        };
        onFileEvent?.({
          type: 'file-added',
          data: fileData,
        });
        return false;
      }

      if (event.type === 'data-fileDeleted') {
        const fileData = event.data as { path: string };
        onFileEvent?.({
          type: 'file-deleted',
          data: fileData,
        });
        return false;
      }

      // Handle tools available event
      if (event.type === 'data-toolsAvailable') {
        const toolsData = event.data as ToolsAvailableEvent;
        onToolsAvailable?.(toolsData);
        return false;
      }

      // Handle system prompt inspection events
      if (event.type === 'data-systemPrompt') {
        onSystemPrompt?.(event.data as SystemPromptEvent);
        return false;
      }
      if (event.type === 'data-systemPromptInjections') {
        onSystemPromptInjections?.(event.data as SystemPromptInjectionsEvent);
        return false;
      }

      // Handle AI SDK custom data events (e.g. tool-timing for unified tool cards)
      if (event.type === 'data' && Array.isArray(event.value)) {
        let handledToolTiming = false;
        for (const item of event.value as Record<string, unknown>[]) {
          if (item.type === 'tool-timing') {
            handledToolTiming = true;
            const id = item.toolCallId as string;
            // Backend sends time.time() (seconds); convert to milliseconds
            const rawStarted = item.startedAt as number | undefined;
            const rawCompleted = item.completedAt as number | undefined;
            toolMetadataRef.current.set(id, {
              startedAt: rawStarted != null ? rawStarted * 1000 : undefined,
              completedAt: rawCompleted != null ? rawCompleted * 1000 : undefined,
              durationMs: item.durationMs as number | undefined,
              category: item.category as string | undefined,
              serverName: item.serverName as string | undefined,
              parentToolCallId: item.parentToolCallId as string | undefined,
            });
          }
        }
        if (handledToolTiming) return false;
      }

      // Handle error
      if (event.type === 'error') {
        throw new Error(
          (event.errorText ||
            event.message ||
            (event.data as { message?: string })?.message ||
            'Agent error') as string,
        );
      }

      // Handle finish-step (intermediate, between tool call steps)
      if (event.type === 'finish-step') {
        // Clean transient activity parts but do NOT terminate the loop
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            const cleanedParts = msg.parts.filter(
              (p) =>
                !(
                  p.type === 'data-activity' &&
                  (p as ActivityPart).transient
                ),
            );
            return { ...msg, parts: cleanedParts };
          }),
        );
        return false;
      }

      // Handle finish (terminal — ends the stream)
      if (event.type === 'finish') {
        // Extract agent metadata from finish event and update message
        const metadata = event.metadata as {
          agent_slug?: string;
          agent_name?: string;
          delegation?: {
            delegated_to: string;
            agent_name: string;
            delegation_time_ms?: number;
          };
        } | undefined;

        // Capture provider response ID for tracing (AgentScope v1.0.18+)
        const invocationId = event.invocationId as string | undefined;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== assistantMessageId) return msg;
            // Remove transient activity parts (T019)
            const cleanedParts = msg.parts.filter(
              (p) =>
                !(
                  p.type === 'data-activity' &&
                  (p as ActivityPart).transient
                ),
            );
            return {
              ...msg,
              parts: cleanedParts,
              ...((metadata || invocationId) && {
                metadata: {
                  ...msg.metadata,
                  ...(metadata && {
                    agent_slug: metadata.agent_slug,
                    agent_name: metadata.agent_name,
                    delegation: metadata.delegation,
                  }),
                  ...(invocationId && { invocationId }),
                },
              }),
            };
          }),
        );
        return true;
      }

      return false;
    },
    [
      onPlanStatusUpdate,
      onFileEvent,
      onCanvasUpdate,
      onCanvasComplete,
      onCanvasError,
      onToolsAvailable,
      onSystemPrompt,
      onSystemPromptInjections,
      addDebugEntry,
    ],
  );

  /**
   * Process a single message through the transport.
   */
  const processStream = useCallback(
    async (messageText: string, isRetry = false): Promise<boolean> => {
      if (!sessionId || !userId) return false;

      abortControllerRef.current = new AbortController();

      if (!isRetry) {
        const userMessage: UIMessage = {
          id: `msg-${Date.now()}`,
          role: 'user',
          parts: [{ type: 'text', text: messageText }],
          createdAt: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
      }
      setStatus('submitted');
      setError(null);

      const assistantMessageId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-assistant`;
      const accumulatedText = { value: '' };
      const accumulatedReasoning = { value: '' };
      const memoryContext: { value: MemoryItem[] | null } = { value: null };
      const hasReceivedToken = { value: false };

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant',
          parts: [],
          createdAt: new Date(),
        },
      ]);

      try {
        ttftStartRef.current = performance.now();
        const stream = transportRef.current.sendMessages({
          chatId: sessionId,
          userId,
          ...(organizationId && { organizationId }),
          message: messageText,
          trigger: 'submit-message',
          abortSignal: abortControllerRef.current.signal,
          ...(extraBody && Object.keys(extraBody).length > 0 && { body: extraBody }),
        });

        for await (const event of stream) {
          const isDone = processStreamEvent(
            event,
            assistantMessageId,
            accumulatedText,
            accumulatedReasoning,
            memoryContext,
            hasReceivedToken,
          );

          if (isDone) break;
        }

        setStatus('ready');
        retryCountRef.current = 0;
        return true;
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          setStatus('ready');
          return true;
        }

        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        setStatus('error');

        addDebugEntry({
          type: 'error',
          content: {
            message: errorObj.message,
            recoverable: retryCountRef.current < MAX_RETRIES,
          },
        });

        return false;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [sessionId, userId, organizationId, extraBody, processStreamEvent, addDebugEntry],
  );

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || messageQueueRef.current.length === 0) return;

    isProcessingRef.current = true;
    const nextMessage = messageQueueRef.current.shift();

    if (nextMessage) {
      let success = await processStream(nextMessage);

      while (!success && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        // Remove the empty assistant message from the failed attempt
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.role === 'assistant' && lastMsg.parts.length === 0) {
            return prev.slice(0, -1);
          }
          return prev;
        });
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY_MS * retryCountRef.current),
        );
        success = await processStream(nextMessage, true);
      }

      if (!success) {
        retryCountRef.current = 0;
      }
    }

    isProcessingRef.current = false;
    processQueue();
  }, [processStream]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !sessionId || !userId) return;

      messageQueueRef.current.push(text.trim());
      processQueue();
    },
    [sessionId, userId, processQueue],
  );

  const stop = useCallback(async () => {
    abortControllerRef.current?.abort();
    isProcessingRef.current = false;
    setStatus('ready');

    if (sessionId && userId) {
      await transportRef.current.stop(sessionId, userId);
    }
  }, [sessionId, userId]);

  const reload = useCallback(async () => {
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === 'user');
    if (!lastUserMessage) return;

    const textPart = lastUserMessage.parts.find(
      (p): p is MessagePart & { type: 'text' } => p.type === 'text',
    );
    if (!textPart) return;

    setMessages((prev) => prev.slice(0, -2));
    await sendMessage(textPart.text);
  }, [messages, sendMessage]);

  const retry = useCallback(async () => {
    setError(null);
    setStatus('ready');
    retryCountRef.current = 0;

    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === 'user');
    if (lastUserMessage) {
      const textPart = lastUserMessage.parts.find(
        (p): p is MessagePart & { type: 'text' } => p.type === 'text',
      );
      if (textPart) {
        setMessages((prev) => prev.slice(0, -1));
        await sendMessage(textPart.text);
      }
    }
  }, [messages, sendMessage]);

  // Tool output support (if transport supports it)
  const addToolOutput = useCallback(
    async (toolCallId: string, output: unknown, isError?: boolean) => {
      if (!sessionId || !userId) return;

      if (transportRef.current.addToolOutput) {
        await transportRef.current.addToolOutput({
          chatId: sessionId,
          userId,
          toolCallId,
          output,
          isError,
        });
      }
    },
    [sessionId, userId],
  );

  // ---- Voice event handling ----
  // Allows voice events (already translated to AI SDK v6 format by the backend)
  // to flow through the same processStreamEvent pipeline as text chat.
  const voiceTurnRef = useRef<{
    assistantMessageId: string | null;
    accumulatedText: { value: string };
    accumulatedReasoning: { value: string };
    memoryContext: { value: MemoryItem[] | null };
    hasReceivedToken: { value: boolean };
    responseStartedAt: number;
  }>({
    assistantMessageId: null,
    accumulatedText: { value: '' },
    accumulatedReasoning: { value: '' },
    memoryContext: { value: null },
    hasReceivedToken: { value: false },
    responseStartedAt: 0,
  });
  // Survives turn reset — used by voice-user-message when transcription
  // arrives after `finish` has already cleared voiceTurnRef.
  const lastVoiceAssistantIdRef = useRef<string | null>(null);

  const handleVoiceEvent = useCallback(
    (event: StreamEvent) => {
      const eventType = event.type as string;

      // voice-turn-start: create empty assistant message, prepare accumulators
      if (eventType === 'voice-turn-start') {
        const assistantMessageId = `msg-voice-${Date.now()}-assistant`;
        voiceTurnRef.current = {
          assistantMessageId,
          accumulatedText: { value: '' },
          accumulatedReasoning: { value: '' },
          memoryContext: { value: null },
          hasReceivedToken: { value: false },
          responseStartedAt: Date.now(),
        };
        lastVoiceAssistantIdRef.current = assistantMessageId;
        setMessages((prev) => [
          ...prev,
          {
            id: assistantMessageId,
            role: 'assistant',
            parts: [],
            createdAt: new Date(),
          },
        ]);
        setStatus('streaming');
        return;
      }

      // voice-user-message: insert user message before the assistant response.
      // Transcription often arrives AFTER the model has already started (or
      // even finished) responding, so we fall back to lastVoiceAssistantIdRef
      // when the active turn has already been cleared by `finish`.
      if (eventType === 'voice-user-message') {
        const text = event.text as string;
        if (!text) return;

        const userMsg: UIMessage = {
          id: `msg-voice-${Date.now()}-user`,
          role: 'user',
          parts: [{ type: 'text', text } as TextPart],
          createdAt: new Date(),
        };

        setMessages((prev) => {
          const targetId =
            voiceTurnRef.current.assistantMessageId ||
            lastVoiceAssistantIdRef.current;
          const assistantIdx = targetId
            ? prev.findIndex((m) => m.id === targetId)
            : -1;
          // Insert user message before the assistant response
          if (assistantIdx > 0) {
            const result = [...prev];
            result.splice(assistantIdx, 0, userMsg);
            return result;
          }
          return [...prev, userMsg];
        });
        return;
      }

      // For all other events, route through processStreamEvent
      const ref = voiceTurnRef.current;
      if (!ref.assistantMessageId) {
        // No active voice turn — log for debug but don't crash
        addDebugEntry({
          type: 'stream-event',
          content: {
            eventType: `voice-orphan:${eventType}`,
            payload: event as Record<string, unknown>,
            category: categorizeEventType(eventType),
          },
        });
        return;
      }

      const isDone = processStreamEvent(
        event,
        ref.assistantMessageId,
        ref.accumulatedText,
        ref.accumulatedReasoning,
        ref.memoryContext,
        ref.hasReceivedToken,
      );

      if (isDone) {
        // Reset voice turn state
        voiceTurnRef.current = {
          assistantMessageId: null,
          accumulatedText: { value: '' },
          accumulatedReasoning: { value: '' },
          memoryContext: { value: null },
          hasReceivedToken: { value: false },
          responseStartedAt: 0,
        };
        setStatus('ready');
      }
    },
    [processStreamEvent, addDebugEntry],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    messages,
    status,
    error,
    isInputDisabled: status === 'streaming' || status === 'submitted',
    sendMessage,
    stop,
    reload,
    retry,
    debugEntries,
    addDebugEntry,
    addToolOutput: transportRef.current.addToolOutput
      ? addToolOutput
      : undefined,
    toolTimings: toolTimingRef.current,
    toolMetadata: toolMetadataRef.current,
    handleVoiceEvent,
    lastPushTimestamp,
    injectAssistantMessage,
  };
}

// Re-export for backwards compatibility
export { useChatStream } from './useChatLegacy';
